// Designer importer for the revise-run slide type.
//
// The registry needs an importer so an outline slide labelled REVISE-RUN with a
// `data` object becomes a slide — that is the path the MCP's import_outline
// already takes for every AI-only type. The run itself must already be valid:
// this importer does structural checks only and pushes the validator's
// findings into `warnings`, because the write-time gate (block on any block)
// belongs to the tool that writes, not to a converter that must always return
// something renderable.

import type { DesignerSlide } from '../lessonImport';
import type { ReviseRunSlideData } from '../../components/course-builder/slides/slideTypes';
import type { ReviseRun } from './types';
import { structuralIssues, validateRun } from './validate';

/** A run that renders as an honest stub rather than crashing the strip. */
export function emptyRun(): ReviseRun {
  return {
    version: 1,
    profile: 'lesson',
    board: 'ieb',
    cover: { eyebrow: 'Kula Revise', title: 'Untitled run', lines: [], begin: 'Begin' },
    screens: [],
    results: { eyebrow: 'Report', title: 'Where you stand', bands: [{ min: 0, label: 'Done', text: '' }] },
  };
}

export function buildReviseRun(ds: DesignerSlide, warnings: string[]): ReviseRunSlideData {
  const raw = (ds as { data?: unknown }).data;
  const structural = structuralIssues(raw);
  if (structural.length) {
    for (const i of structural) warnings.push(`revise-run: ${i.message}`);
    return {
      template: 'revise-run',
      title: ds.text || 'Revise run',
      subtitle: '',
      run: emptyRun(),
    };
  }
  const run = raw as ReviseRun;
  const v = validateRun(run);
  for (const b of v.blocks) warnings.push(`revise-run BLOCK: ${b.message}`);
  for (const w of v.warnings) warnings.push(`revise-run: ${w.message}`);
  return {
    template: 'revise-run',
    title: run.cover.title,
    subtitle: run.cover.eyebrow,
    run,
  };
}
